import React, { useState } from 'react';
import { Plus, AlertCircle, ArrowLeft, Upload, X } from 'lucide-react';
import { User } from '../../types';
import { DevOpsTaskType, DevOpsTaskPriority } from '../../types/devopsTasks';
import { getApiUrl } from '../../config/api';
import { createNotification } from '../../utils/api';
import { todayLocal } from '../../utils/localDate';
import { toast } from '../../utils/toast';
import { readApiError } from '../common/apiError';

interface CreateTaskFormProps {
  currentUser: User;
  onTaskCreated: () => void;
  onBack?: () => void;
}

const CreateTaskForm: React.FC<CreateTaskFormProps> = ({ currentUser, onTaskCreated, onBack }) => {
  const [devopsUsers, setDevopsUsers] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<User[]>([]);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    taskType: 'server-issue' as DevOpsTaskType,
    priority: 'medium' as DevOpsTaskPriority,
    affectedEnvironment: '',
    assignedTo: '',
    expectedCompletionDate: '',
    expectedCompletionTime: ''
  });

  React.useEffect(() => {
    fetchAllUsers();
  }, []);

  const fetchAllUsers = async () => {
    try {
      const response = await fetch(getApiUrl('api/users'));
      if (!response.ok) {
        console.error('Error fetching users:', await readApiError(response));
        return;
      }
      const users = await response.json();
      if (!Array.isArray(users)) return;
      
      // Filter DevOps users
      const devops = users.filter((u: any) => 
        ((u.realmRoles || u.roles || []).some((r: string) => r.toLowerCase() === 'devops') || 
        u.role === 'devops')
      );
      setDevopsUsers(devops);
      
      // Store all users for team member selection
      setAllUsers(users);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const selectedDevops = devopsUsers.find(d => d.id === formData.assignedTo);
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      const taskFormData = new FormData();
      
      // Add basic task data
      taskFormData.append('title', formData.title);
      taskFormData.append('description', formData.description);
      taskFormData.append('taskType', formData.taskType);
      taskFormData.append('priority', formData.priority);
      taskFormData.append('affectedEnvironment', formData.affectedEnvironment);
      if (formData.expectedCompletionDate) {
        // Combine date and time if both provided
        let completionDateTime = formData.expectedCompletionDate;
        if (formData.expectedCompletionTime) {
          completionDateTime = `${formData.expectedCompletionDate}T${formData.expectedCompletionTime}`;
        }
        taskFormData.append('expectedCompletionDate', completionDateTime);
      }
      
      // Add created by info
      taskFormData.append('createdById', currentUser.id);
      taskFormData.append('createdByName', currentUser.name || currentUser.username || currentUser.email);
      taskFormData.append('createdByEmail', currentUser.email);
      taskFormData.append('createdByRole', currentUser.role);
      
      // Add assigned to info if selected
      if (selectedDevops) {
        taskFormData.append('assignedToId', selectedDevops.id);
        taskFormData.append('assignedToName', selectedDevops.name || selectedDevops.username || selectedDevops.email);
        taskFormData.append('assignedToEmail', selectedDevops.email);
        taskFormData.append('status', 'assigned');
      } else {
        taskFormData.append('status', 'new');
      }
      
      // Add team members if any selected
      if (selectedTeamMembers.length > 0) {
        taskFormData.append('teamMembers', JSON.stringify(selectedTeamMembers.map(member => ({
          id: member.id,
          name: member.name || member.username || member.email,
          email: member.email,
          role: member.role
        }))));
      }
      
      // Add attachments
      attachments.forEach((file) => {
        taskFormData.append('attachments', file);
      });

      const response = await fetch(getApiUrl('api/tasks'), {
        method: 'POST',
        body: taskFormData
      });

      if (!response.ok) {
        // Keep everything the user typed; surface the server's reason
        const message = await readApiError(response, 'Failed to create task');
        setSubmitError(message);
        toast(message, 'error');
        return;
      }

      // Create in-app notification for creator
      try {
        await createNotification(currentUser.id, 'Task Created', `Your task "${formData.title}" was created successfully.`);
      } catch (nerr) {
        console.warn('Failed to create in-app notification:', nerr);
      }

      // Notify assigned team members
      if (selectedDevops && selectedDevops.id) {
        try {
          await createNotification(selectedDevops.id, 'New Task Assigned', `A new task "${formData.title}" was assigned to you.`);
        } catch (nerr) {
          console.warn('Failed to notify DevOps:', nerr);
        }
      }
      
      // Notify all selected team members
      for (const member of selectedTeamMembers) {
        try {
          await createNotification(member.id, 'New Task Assignment', `You have been added to task "${formData.title}" as a team member.`);
        } catch (nerr) {
          console.warn(`Failed to notify ${member.name}:`, nerr);
        }
      }
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        taskType: 'server-issue',
        priority: 'medium',
        affectedEnvironment: '',
        assignedTo: '',
        expectedCompletionDate: '',
        expectedCompletionTime: ''
      });
      setAttachments([]);
      setSelectedTeamMembers([]);

      toast('Task created', 'success');
      onTaskCreated();
    } catch (error) {
      console.error('Error creating task:', error);
      const message = 'Failed to create task — network error. Please try again.';
      setSubmitError(message);
      toast(message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setAttachments(prev => [...prev, ...newFiles]);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back Button */}
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-700 font-semibold text-lg mb-4"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Dashboard
        </button>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Plus className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Create DevOps Task</h1>
            <p className="text-gray-600">Submit a task for the DevOps team</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Task Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Server test8 not responding"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Task Type *
              </label>
              <select
                value={formData.taskType}
                onChange={(e) => setFormData({ ...formData, taskType: e.target.value as DevOpsTaskType })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="server-issue">🖥️ Server Issue</option>
                <option value="environment-setup">🚀 Environment Setup</option>
                <option value="configuration">🔧 Configuration</option>
                <option value="bug-investigation">🐛 Bug Investigation</option>
                <option value="infrastructure">📦 Infrastructure</option>
                <option value="maintenance">🔄 Maintenance</option>
                <option value="other">❓ Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority *
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as DevOpsTaskPriority })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="critical">🔴 Critical - System Down (1 hour SLA)</option>
                <option value="high">🟠 High - Major Issue (4 hours SLA)</option>
                <option value="medium">🟡 Medium - Standard (1 day SLA)</option>
                <option value="low">🟢 Low - Non-urgent (3 days SLA)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Describe the issue or task in detail..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Affected Environment (Optional)
            </label>
            <input
              type="text"
              value={formData.affectedEnvironment}
              onChange={(e) => setFormData({ ...formData, affectedEnvironment: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., test8.laudea.in, production server"
            />
          </div>

          {/* Team Assignment Section */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">👥</span> Team Assignment (Optional)
            </h3>
            <p className="text-sm text-gray-600 mb-4">Assign team members who will be involved in this task</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign to DevOps
                </label>
                <select
                  value={formData.assignedTo}
                  onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Not assigned (any DevOps can assign)</option>
                  {devopsUsers.map(devops => (
                    <option key={devops.id} value={devops.id}>
                      {devops.name || devops.username}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add Team Members
                </label>
                <select
                  onChange={(e) => {
                    const userId = e.target.value;
                    if (userId && !selectedTeamMembers.find(m => m.id === userId)) {
                      const user = allUsers.find(u => u.id === userId);
                      if (user) {
                        setSelectedTeamMembers([...selectedTeamMembers, user]);
                      }
                    }
                    e.target.value = '';
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select team member...</option>
                  {allUsers
                    .filter(user => !selectedTeamMembers.find(m => m.id === user.id))
                    .map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name || user.username} ({user.role})
                      </option>
                    ))}
                </select>
                {selectedTeamMembers.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {selectedTeamMembers.map(member => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-lg"
                      >
                        <span className="text-sm font-medium text-gray-900">
                          {member.name || member.username} <span className="text-xs text-gray-600">({member.role})</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => setSelectedTeamMembers(selectedTeamMembers.filter(m => m.id !== member.id))}
                          className="text-red-600 hover:text-red-800 font-bold"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Expected Completion Section */}
          <div className="border-t pt-6 mt-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expected Completion (Optional)
              </label>
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 flex items-center">
                      <span className="mr-1">📅</span> Date
                    </label>
                    <input
                      type="date"
                      value={formData.expectedCompletionDate}
                      onChange={(e) => setFormData({ ...formData, expectedCompletionDate: e.target.value })}
                      min={todayLocal()}
                      className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:border-gray-400 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 flex items-center">
                      <span className="mr-1">🕐</span> Time
                    </label>
                    <select
                      value={formData.expectedCompletionTime}
                      onChange={(e) => setFormData({ ...formData, expectedCompletionTime: e.target.value })}
                      className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:border-gray-400 bg-white"
                    >
                      <option value="">Select Time</option>
                      <option value="00:00">12:00 AM</option>
                      <option value="00:30">12:30 AM</option>
                      <option value="01:00">01:00 AM</option>
                      <option value="01:30">01:30 AM</option>
                      <option value="02:00">02:00 AM</option>
                      <option value="02:30">02:30 AM</option>
                      <option value="03:00">03:00 AM</option>
                      <option value="03:30">03:30 AM</option>
                      <option value="04:00">04:00 AM</option>
                      <option value="04:30">04:30 AM</option>
                      <option value="05:00">05:00 AM</option>
                      <option value="05:30">05:30 AM</option>
                      <option value="06:00">06:00 AM</option>
                      <option value="06:30">06:30 AM</option>
                      <option value="07:00">07:00 AM</option>
                      <option value="07:30">07:30 AM</option>
                      <option value="08:00">08:00 AM</option>
                      <option value="08:30">08:30 AM</option>
                      <option value="09:00">09:00 AM</option>
                      <option value="09:30">09:30 AM</option>
                      <option value="10:00">10:00 AM</option>
                      <option value="10:30">10:30 AM</option>
                      <option value="11:00">11:00 AM</option>
                      <option value="11:30">11:30 AM</option>
                      <option value="12:00">12:00 PM</option>
                      <option value="12:30">12:30 PM</option>
                      <option value="13:00">01:00 PM</option>
                      <option value="13:30">01:30 PM</option>
                      <option value="14:00">02:00 PM</option>
                      <option value="14:30">02:30 PM</option>
                      <option value="15:00">03:00 PM</option>
                      <option value="15:30">03:30 PM</option>
                      <option value="16:00">04:00 PM</option>
                      <option value="16:30">04:30 PM</option>
                      <option value="17:00">05:00 PM</option>
                      <option value="17:30">05:30 PM</option>
                      <option value="18:00">06:00 PM</option>
                      <option value="18:30">06:30 PM</option>
                      <option value="19:00">07:00 PM</option>
                      <option value="19:30">07:30 PM</option>
                      <option value="20:00">08:00 PM</option>
                      <option value="20:30">08:30 PM</option>
                      <option value="21:00">09:00 PM</option>
                      <option value="21:30">09:30 PM</option>
                      <option value="22:00">10:00 PM</option>
                      <option value="22:30">10:30 PM</option>
                      <option value="23:00">11:00 PM</option>
                      <option value="23:30">11:30 PM</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-gray-500 flex items-center">
                  <span className="mr-1">💡</span>
                  <span>Select date from calendar and time from dropdown (30-minute intervals)</span>
                </p>
              </div>
            </div>
          </div>

          {/* File Upload Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Attach Screenshots/Files (Optional)
            </label>
            <div className="flex items-center space-x-3">
              <label className="flex items-center px-4 py-2 bg-white border-2 border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-all">
                <Upload className="w-5 h-5 mr-2 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Choose Files</span>
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
              <span className="text-sm text-gray-500">
                {attachments.length > 0 ? `${attachments.length} file(s) selected` : 'No files selected'}
              </span>
            </div>
            
            {/* Selected Files Preview */}
            {attachments.length > 0 && (
              <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                {attachments.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 border border-gray-300 rounded-lg px-3 py-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                        <Upload className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{file.name}</p>
                        <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="p-1 hover:bg-red-100 rounded-full transition-all"
                    >
                      <X className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-gray-700">
                <p className="font-medium text-gray-900 mb-1">Task Creation Info</p>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>DevOps team will be notified about new tasks</li>
                  <li>You can track task progress and add comments</li>
                  <li>Unassigned tasks can be taken by any DevOps member</li>
                </ul>
              </div>
            </div>
          </div>

          {submitError && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" role="alert">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{submitError}</span>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                if (confirm('Clear all fields?')) {
                  setFormData({
                    title: '',
                    description: '',
                    taskType: 'server-issue',
                    priority: 'medium',
                    affectedEnvironment: '',
                    assignedTo: '',
                    expectedCompletionDate: '',
                    expectedCompletionTime: ''
                  });
                  setAttachments([]);
                  setSelectedTeamMembers([]);
                }
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Clear
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating...' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTaskForm;
